import api from "@/services/api.service";
import Vue from "vue";
import { formatRegionUrl } from "@/utils/string-utils";
import { ActionTree, Dispatch } from "vuex";
import { LoginStatus, Region, RootState } from "../types";
import { RegionState } from "./types";

/**
 * Starts a heartbeat session for a specified region, and returns the interval ID. If an error occurs while attempting
 * to heartbeat, the user (and components) will be notified, logged out and cleared.
 */
function startHeartbeatForRegion(dispatch: Dispatch, region: Region) {
	var regionalHeartbeatInterval = setInterval(async () => {
		try {
			const { status } = await api.heartbeatToServer(region.url);

			if (status != 200) {
				throw new Error();
			}
		} catch (err) {
			Vue.notify({
				type: "error",
				title: "Remote Session Expired",
				text: `Your remote session to the region ${region.title} has expired, please try re-connecting or contact your support provider if the problem persists`
			});

			// Clear the interval to stop further heartbeats.
			clearInterval(regionalHeartbeatInterval);

			// Forcefully log the user out of the region
			await dispatch("forceLogoutRegion", region.regionId);
		}
	}, 30_000);

	return regionalHeartbeatInterval;
}

export const actions: ActionTree<RegionState, RootState> = {
	/**
	 * Loads the regions available to the current user.
	 * @param param0 The vuex commit method.
	 */
	async loadRegions({ commit }) {
		const regions = await api.getRegions();
		commit("setRegions", regions);
	},

	/**
	 * Logs the user into a remote region specified by id and url, and optionally overwrites the local login with the
	 * remote result. If we're not overwriting the local login, a heartbeat will be set up to the remote server.
	 * @param param0 The vuex state, getters, commit, and dispatch methods.
	 * @param param1 The id and url of the region, and optionally, whether or not we should overwrite the local login.
	 */
	async loginToRegion({ dispatch, commit, state, getters }, { regionId, regionUrl, overwriteLocalLogin }) {
		regionUrl = regionUrl || formatRegionUrl(getters.getRegionUrl(regionId));

		if (!overwriteLocalLogin && typeof state.logins[regionId] != "undefined") {
			// If we're not overwriting the local login, and we already have an active user session in the remote
			// region, then just increment the reference count and return.
			commit("incrementLoginReferenceCount", regionId);
			return state.logins[regionId].userLoginResult;
		}

		const loginToken = await api.getInterDataServiceLoginToken(regionId);
		const {
			data: { loginResult, xsrfToken }
		} = await api.loginToRegion(loginToken, regionUrl);

		if (loginResult.status == LoginStatus.Success) {
			// Store the token
			commit("setXsrfToken", { regionUrl, xsrfToken });

			loginResult.isFullUser = true;

			if (overwriteLocalLogin) {
				// Backup the local login and overwrite it with the region login result. This logic will only ever
				// happen if we're switching the entire app over to the remote region, typically when the user
				// picks up a remote alarm.
				commit("storeLocalLogin", null, { root: true });
				commit("login", loginResult, { root: true });
			} else {
				const region = state.regions[regionId];

				// Store the login against the id of the region, and setup a heartbeat.
				var heartbeat = startHeartbeatForRegion(dispatch, region);
				commit("setLogin", { regionId, loginResult, heartbeat });
			}

			return loginResult;
		} else {
			throw loginResult;
		}
	},

	/**
	 * Logs out the user in a remote region if there are no more references to it, specified by id.
	 * @param param0 The vuex state, commit, and getter methods.
	 * @param regionId The id of the region.
	 */
	async logoutRegion({ commit, dispatch, state, getters }, regionId) {
		if (typeof state.logins[regionId] == "undefined") {
			return;
		}

		commit("decrementLoginReferenceCount", regionId);

		// Only log the user out if there are no more references to the remote session.
		if (state.logins[regionId].referenceCount <= 0) {
			await dispatch("forceLogoutRegion", regionId);
		}
	},

	/**
	 * Forcefully logs the user out of a specified region id, regardless of whether or not it is being referenced.
	 * @param param0 The vuex state, commit, and getters.
	 * @param regionId The id of the region.
	 */
	async forceLogoutRegion({ commit, state, getters }, regionId) {
		if (typeof state.logins[regionId] == "undefined" || regionId == 0) {
			return;
		}

		const regionUrl = getters.getRegionUrl(regionId);

		// Stop any heartbeats.
		clearInterval(state.logins[regionId].heartbeat);

		// Perform the logout.
		await api.logoutRegion(regionUrl);

		// Clear the store.
		commit("clearLogin", regionId);
		commit("clearXsrfToken", regionUrl);
	},

	/**
	 * Logs the user out of all the regions they're currently connected too.
	 * @param param0 The vuex state.
	 */
	async logoutAllRegions({ dispatch, state }) {
		const loginRegions = Object.keys(state.logins);
		for (var regionId in loginRegions) {
			await dispatch("logoutRegion", parseInt(regionId));
		}
	},

	/**
	 * Loads all of the regions for mutation.
	 * @param param0 The vuex commit.
	 */
	async loadAllRegions({ commit }): Promise<void> {
		commit("setAllRegions", await api.getAllRegions());
	},

	/**
	 * Creates the new region in the database and adds the region to the state.
	 * @param param0 The vuex commit.
	 * @param region Region to be created.
	 */
	async createRegion({ commit }, region: Region): Promise<void> {
		commit("setRegion", await api.createRegion(region));
	},

	/**
	 * Updates the region in the database and in the state.
	 * @param param0 The vuex commit.
	 * @param region Region to be updated.
	 */
	async updateRegion({ commit }, region: Region): Promise<void> {
		commit("setRegion", await api.updateRegion(region));
	},

	/**
	 * Deletes the region in the database and deletes the region from the state.
	 * @param param0 The vuex commit.
	 * @param region Region to be deleted.
	 */
	async deleteRegion({ commit }, regionId: number): Promise<void> {
		commit("removeRegion", await api.deleteRegion(regionId));
	}
};
